<script setup>
import {onMounted, reactive, ref, watch} from 'vue'
import {Feedback, Progress, State, can, clone, getModels, go} from '@/paks/vu-app'
import DashEdit from './DashEdit.vue'
import ExportDash from './ExportDash.vue'
import ImportDash from './ImportDash.vue'
import {cleanBoard} from './BoardUtils.js'
import UUID from '@/paks/js-uuid'

const Fields = [
    {name: 'edit', icon: '$edit', width: '5%'},
    {name: 'designx', title: 'Design', icon: '$dashboard', width: '5%'},
    {name: 'show', icon: '$launch', width: '5%'},
    {name: 'name'},
    {name: 'layout', title: 'Grid Layout', desktop: true, icon: (v) => (v == 'grid' ? '$check' : ''), width: '5%'},
    {name: 'toolbar', desktop: true, icon: (v) => (v ? '$check' : ''), width: '5%'},
    {
        name: 'framed',
        title: 'Framed',
        desktop: true,
        icon: (v) => (v ? '$check' : ''),
        desktop: true,
        width: '5%',
    },
    {name: 'refresh'},
    {name: 'widgets', format: (v, rec) => rec.widgets?.length, desktop: true},
]

const page = reactive({
    builder: State.config.builder,
    dashboard: null,
    dashboards: null,
    fields: Fields,
    helper: null,
    saving: false,
    showDashEdit: false,
    showImport: false,
    showExport: false,
})

const table = ref(null)
const {Dashboard} = getModels()

watch(() => State.app.needs.updateBoard || State.app.needs.reload, () => load())

onMounted(() => {
    page.select = {
        actions: {Add: 0, Edit: 1, Delete: 2, Export: 1, Import: 0},
        multi: true,
    }
    load()
})

function load() {
    page.dashboards = clone(State.cache.dashboards)
}

async function save() {
    Progress.run(async () => {
        for (let name of Object.keys(State.app.needs.save || {})) {
            let dashboard = State.cache.dashboards.find((d) => d.name == name)
            if (dashboard) {
                await Dashboard.update(dashboard, {exists: true})
            }
        }
        for (let dashboard of State.cache.removedDashboards) {
            try {
                await Dashboard.remove({id: dashboard.id})
            } catch (e) {}
        }
        State.cache.removedDashboards = []
        State.app.noNeed('save')
    }, {page, field: 'saving', feedback: 'Dashboards Saved'})
}

async function clicked({action, item, items, column, rec, row}) {
    if (action == 'add') {
        if (!can('admin')) {
            return
        }
        editBoard({})
        load()
    } else if (action == 'import') {
        editImport({})
    } else if (action == 'export') {
        editExport(item)
    } else if (action == 'edit' || (action == 'cell' && column.name == 'edit')) {
        if (can('admin')) {
            editBoard(item)
            load()
        }
    } else if (action == 'cell') {
        if (column.name == 'designx') {
            await Dashboard.set(item)
            State.ref.router.push({name: 'dash-editor', query: {design: true}}).catch(() => {})
        } else {
            await Dashboard.set(item)
            go({name: 'show-dash', params: {id: item.id}})
        }
    }
}

async function editBoard(dashboard) {
    page.showDashEdit = dashboard ? true : false
    if (dashboard) {
        dashboard = Object.assign(
            {
                id: UUID(),
                range: {},
                css: [],
                widgets: [],
                widgetCss: [],
                framed: true,
                layout: 'grid',
                toolbar: true,
            },
            dashboard
        )
        page.dashboard = cleanBoard(dashboard)
    } else {
        page.dashboard = null
    }
    load()
}

async function editImport(dashboard) {
    page.dashboard = dashboard
    page.showImport = dashboard ? true : false
}

async function editExport(dashboard) {
    page.dashboard = dashboard
    page.showExport = dashboard ? true : false
}
</script>

<template>
    <div class="dash-list page">
        <vu-help url="/doc/ui/dashboard/dashboard-list.html" />
        <vu-table
            :data="page.dashboards"
            :fields="page.fields"
            :select="page.select"
            name="dashboards"
            nodata=" "
            options="dynamic,toolbar"
            ref="table"
            sort="name:asc"
            width="100%"
            @click="clicked"
            @reload="load">
            <template v-slot:more="props">
                <v-btn
                    v-if="State.app.needs.save"
                    color="primary"
                    class="mr-4"
                    :loading="page.saving"
                    @click="save">
                    Save
                </v-btn>
                <v-btn v-if="can('admin')" color="primary" @click="editBoard({})">Add</v-btn>
            </template>
        </vu-table>

        <vu-panel v-model="page.showDashEdit" :keyboard="false" :widths="['550px']" @close="editBoard">
            <DashEdit v-model="page.dashboard" @close="editBoard" />
        </vu-panel>
        <vu-panel v-model="page.showImport" :keyboard="false" :widths="['550px']" @close="editImport">
            <ImportDash @close="editImport" />
        </vu-panel>
        <vu-panel v-model="page.showExport" :keyboard="false" :widths="['550px']" @close="editExport">
            <ExportDash :dashboard="page.dashboard" @close="editExport" />
        </vu-panel>
    </div>
</template>

<style lang="scss">
.dash-list {
    _max-width: 900px;
}
</style>
